import React from 'react';
import { Box, Typography } from '@mui/material';
import { webConfig } from '../../utils/webConfig';
import { getDefaultBannerImageUrl } from '../Donee/utils';
import { AddressIcon, PhoneIcon, VerifiedIcon } from '../icons';
import { useDonee } from '../../api';

export const OrganizationCard: React.FC = () => {
    const { donee } = useDonee();
    const organization = donee?.organization;
    if (!organization) return null;
    const isBlocked = webConfig.isBlockedDonee(donee.idShort);
    const defaultCoverUrl = getDefaultBannerImageUrl(
        organization.type,
        organization.isPaymentVerified,
    );
    const profileUrl = organization.official?.picture;

    return (
        <div
            id="organization-card"
            className="max-w-[360px] max-h-[700px] bg-white rounded-[32px] shadow-high overflow-hidden"
        >
            <Box
                id="organization-card-cover-photo"
                component="img"
                alt={organization.name}
                src={
                    isBlocked || !organization.banner
                        ? defaultCoverUrl
                        : organization.banner
                }
                onError={(event) => {
                    event.currentTarget.src = defaultCoverUrl;
                }}
                className="w-full h-[202px] object-cover"
            />
            {profileUrl && !isBlocked && (
                <Box
                    id="organization-card-profile-photo"
                    component="img"
                    alt={`${organization.name} profile`}
                    src={profileUrl}
                    onError={(event) => {
                        event.currentTarget.src = webConfig.doneeAvatarDefault;
                    }}
                    className="w-[72px] h-[72px] -mt-9 mb-2 ml-7 rounded-full object-cover border-4 border-white [filter:drop-shadow(0px_0px_10px_rgba(0,0,0,0.25))]"
                />
            )}
            <div
                id="organization-card-content"
                className="px-5 pb-5"
            >
                <div
                    id="organization-card-info"
                    className="mt-3 ml-3"
                >
                    <div
                        id="organization-card-name-row"
                        className="mb-3"
                    >
                        <Typography
                            id="organization-card-name"
                            className="text-heading-large text-primary inline"
                        >
                            {isBlocked ? 'Unknown' : organization.name}
                        </Typography>
                        {organization.isPaymentVerified && (
                            <div
                                id="organization-card-verified-badge"
                                className="inline-block ml-2 align-middle"
                            >
                                <VerifiedIcon id="organization-card-verified-icon" />
                            </div>
                        )}
                    </div>
                    <div
                        id="organization-card-official-name-wrapper"
                        className="overflow-hidden"
                    >
                        {organization.official?.name && (
                            <Typography
                                id="organization-card-official-name"
                                className="text-body-large text-secondary mb-8"
                            >
                                {isBlocked
                                    ? 'Unknown'
                                    : organization.official.name}
                            </Typography>
                        )}
                    </div>
                    <Typography
                        id="organization-card-address"
                        className="text-body-medium-bold text-secondary flex items-center mb-[18px]"
                    >
                        <AddressIcon
                            id="organization-card-address-icon"
                            className="mr-[15px] shrink-0"
                        />
                        {isBlocked ? (
                            <span id="organization-card-address-unknown">
                                Unknown
                            </span>
                        ) : (
                            <span id="organization-card-address-value">
                                {organization.streetAddress}
                                <br />
                                {organization.city}, {organization.state}{' '}
                                {organization.zip}
                            </span>
                        )}
                    </Typography>
                    <Typography
                        id="organization-card-phone"
                        className="text-body-medium-bold text-secondary flex items-center mb-[42px]"
                    >
                        <PhoneIcon
                            id="organization-card-phone-icon"
                            className="ml-px mr-[14px] shrink-0"
                        />
                        <span
                            id="organization-card-phone-value"
                            className="ml-0.5"
                        >
                            {isBlocked ? 'Unknown' : organization.phone}
                        </span>
                    </Typography>
                </div>
                {organization.isTaxDeductible && !isBlocked && (
                    <div
                        id="organization-card-tax-banner"
                        className="p-3 text-center rounded-3xl bg-[#FAFAFC]"
                    >
                        <Typography
                            id="organization-card-tax-banner-text"
                            className="text-body-medium text-secondary"
                        >
                            Tax deductible organization
                        </Typography>
                    </div>
                )}
            </div>
        </div>
    );
};
